import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FiHelpCircle } from 'react-icons/fi'
import { NumberFormatValues } from 'react-number-format'

import { useQuery } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import Tooltip from '~/components/Tooltip'
import { Badge } from '~/components/UI/Badge'
import { FormGroup, InputMoney, Label } from '~/components/unform'
import { formatMoney, formatMoneyV2, unformatMoney } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { getMinCostService } from '~/modules/retailMedia/services/campaign/min-cost'

import { useCampaignForm } from '../../hooks/useCampaignForm'
import { DTOPayloadMinCostsOffsite } from './dto'

const StandardInvestment = ({
  adType,
  publisherId,
  formData,
  formIsValid,
  handleIsValid,
  initialValueDailyBudget,
  formRef
}: OffsiteInvestmentProps<FormHandles>) => {
  const { isWhiteLabel, lengthCentsCurrency } = useAppInfo()
  const { selectedAudience, campaignPeriod, campaignPermissions } =
    useCampaignForm()

  const [CPC, setCPC] = useState(0)
  const [CPM, setCPM] = useState(0)
  const [minDailyBudget, setMinDailyBudget] = useState(0)

  const [budgetValueIsValid, setBudgetValueIsValid] = useState(false)
  const [dailyBudgetIsValid, setDailyBudgetIsValid] = useState(false)

  const [totalBudget, setTotalBudget] = useState(0)

  /**
   * Checa se a seção está pronta para uso.
   * O formulário precisa ser válido para fazer a consulta dos custo mínimos.
   * Também precisa ter un publisher definido.
   */
  const isReady = useMemo(
    () => formIsValid.isValid && (!!publisherId || !!isWhiteLabel),
    [formIsValid.isValid, publisherId, isWhiteLabel]
  )

  /**
   * < Handle min costs >
   */
  const payload = useMemo(() => {
    const data: Partial<PayloadByType> = {
      ad_type: adType,
      name: formData?.name,
      ...DTOPayloadMinCostsOffsite[adType as keyof IDTOMinCostsOffsite](
        formData as DTOPayloadMinCostsOffsiteData
      )
    }

    if (publisherId) {
      data.publisher_id = publisherId
    }

    return data
  }, [adType, formData, publisherId])

  const params: GetMinCostsRequest = useMemo(
    () => ({
      body: isReady ? payload : ({} as GetMinCostsRequestBody)
    }),
    [isReady, payload]
  )

  const { data, error } = useQuery({
    queryKey: ['minCosts', params],
    queryFn: async () => {
      const response = await getMinCostService(params)

      return response.data
    },
    enabled: isReady,
    refetchOnWindowFocus: true,
    placeholderData: previousData => previousData
  })

  /**
   * <Handle dynamic inputs>
   * Populando os campos de acordo com os valores mínimos da API se estiverem vazios
   */

  const handleDynamicInputs = useCallback(() => {
    const transformToMoney = Object.keys(data).reduce((acc, key) => {
      const value = data[key as keyof GetMinCostsServiceResponse]

      return {
        ...acc,
        [key]: formatMoneyV2({ value: value as unknown as number })
      }
    }, {} as GetMinCostsServiceResponse)

    const currentValues = formRef?.current?.getData()

    if (!currentValues?.budget?.daily?.value) {
      formRef?.current?.setFieldValue(
        'budget.daily',
        transformToMoney.min_daily_budget
      )
    }

    if (!currentValues?.budget?.cpc?.value) {
      formRef?.current?.setFieldValue('budget.cpc', transformToMoney.min_cpc)
    }

    if (!currentValues?.budget?.cpm?.value) {
      formRef?.current?.setFieldValue('budget.cpm', transformToMoney.min_cpm)
    }
  }, [data, formRef])

  useEffect(() => {
    if (data) {
      handleDynamicInputs()
    }
  }, [data, handleDynamicInputs])

  /** </ Handle dynamic inputs > */

  /**
   * <Handle cost>
   * Função para manipular os valores de orçamento total, diário, cpc e cpm
   */
  type HandleCostTypes = 'total' | 'daily' | 'cpc' | 'cpm'
  type HandleCostHandler = {
    setter: React.Dispatch<React.SetStateAction<number>>
    min?: number
    setterIsValid?: React.Dispatch<React.SetStateAction<boolean>>
  }
  type HandleCostHandlers = {
    [key in HandleCostTypes]: HandleCostHandler
  }

  const handleCost = useCallback(
    (inputData: NumberFormatValues, type: HandleCostTypes) => {
      const amount = inputData.value
      const value = lengthCentsCurrency ? Number(amount) / 100 : Number(amount)

      const handlers: HandleCostHandlers = {
        cpc: {
          setter: setCPC,
          min: data?.min_cpc,
          setterIsValid: setBudgetValueIsValid
        },
        cpm: {
          setter: setCPM,
          min: data?.min_cpm,
          setterIsValid: setBudgetValueIsValid
        },
        daily: {
          setter: setMinDailyBudget,
          min: data?.min_daily_budget,
          setterIsValid: setDailyBudgetIsValid
        },
        total: {
          setter: setTotalBudget
        }
      }

      const setter = handlers?.[type]?.setter
      const setterIsValid = handlers?.[type]?.setterIsValid
      const min = handlers?.[type]?.min

      setter(value)

      if (setterIsValid && min) {
        setterIsValid(value >= min)
      }
    },
    [data?.min_cpc, data?.min_cpm, data?.min_daily_budget, lengthCentsCurrency]
  )
  /** </Handle cost> */

  /**
   * <Handle total budget>
   * Função para manipular o orçamento total baseado no orçamento diário
   * e o tempo de duração da campanha.
   */
  const handleSuggestedDailyBudget = useCallback(() => {
    if (campaignPeriod?.endDate) {
      const duration = campaignPeriod?.duration || 1
      const totalBudgetValue = totalBudget / duration
      const formattedValue = formatMoneyV2({ value: totalBudgetValue })
      formRef?.current?.setFieldValue('budget.daily', formattedValue)
    }
  }, [campaignPeriod?.endDate, campaignPeriod?.duration, totalBudget, formRef])

  useEffect(() => {
    if (totalBudget && campaignPeriod?.endDate) {
      handleSuggestedDailyBudget()
    }
  }, [campaignPeriod?.endDate, handleSuggestedDailyBudget, totalBudget])
  /** </Handle total budget> */

  /**
   * Escuta mudanças para enviar para o form
   * Caso a API dê erro, não travamos a criação da campanha.
   * Deixamos criar e depois o ajuste de valor é feito.
   */
  useEffect(() => {
    if (error) {
      handleIsValid(true)
      return
    }

    handleIsValid(budgetValueIsValid && dailyBudgetIsValid)
  }, [budgetValueIsValid, dailyBudgetIsValid, error, handleIsValid])

  /**
   * Formata o valor inicial do input de orçamento diario e envia
   * para a função de handle para a validação
   */
  useEffect(() => {
    if (initialValueDailyBudget) {
      const valueDailyBudget = unformatMoney(initialValueDailyBudget)
      const value = Number(valueDailyBudget) * 100

      setMinDailyBudget(value)

      const initialValue = {
        formattedValue: initialValueDailyBudget,
        value: `${value}`,
        floatValue: value
      }

      handleCost(initialValue, 'daily')
    }
  }, [initialValueDailyBudget, data, handleCost])

  return (
    <fieldset className="d-flex flex-column gap-2 justify-start">
      {!formIsValid.isValid && (
        <Alert variant="warning">
          {t('rm:campaign.form.alerts.fillAllFields')}
        </Alert>
      )}

      {campaignPermissions?.allow_total_budget_campaign && (
        <FormGroup>
          <Label htmlFor="budget.total" helperText={t('rm:totalBudget.helper')}>
            {t('rm:totalBudget')}{' '}
            <Badge
              text="beta"
              size="small"
              className="ms-1"
              template="warning"
            />
          </Label>
          <InputMoney
            id="budget.total"
            name="budget.total"
            disabled={!isReady}
            onChange={data => handleCost(data, 'total')}
          />
        </FormGroup>
      )}

      <FormGroup>
        <Label
          htmlFor="budget.daily"
          isRequired
          helperText={t('rm:dailyBudget.helper.text')}
        >
          {t('rm:dailyBudget')}
          {data?.min_daily_budget >= 0
            ? ` (${t('rm:minimum')}: ${formatMoney(data.min_daily_budget)})`
            : null}
        </Label>
        <InputMoney
          id="budget.daily"
          name="budget.daily"
          required
          disabled={!isReady}
          onChange={data => handleCost(data, 'daily')}
          showBadgeIsValid={minDailyBudget >= data?.min_daily_budget}
          showBadgeIsInvalid={minDailyBudget < data?.min_daily_budget}
        />
      </FormGroup>

      {(adType === 'product' || adType === 'sponsored_brand') && (
        <FormGroup className="border-top pt-2">
          <Label
            htmlFor="budget.cpc"
            isRequired
            helperText={t('rm:cpc.description')}
          >
            CPC Max
            {data?.min_cpc >= 0
              ? ` (${t('rm:minimum')}: ${formatMoney(data.min_cpc)})`
              : null}
          </Label>

          <InputMoney
            id="budget.cpc"
            name="budget.cpc"
            required
            disabled={!isReady}
            onChange={data => handleCost(data, 'cpc')}
            showBadgeIsValid={CPC >= data?.min_cpc}
            showBadgeIsInvalid={CPC < data?.min_cpc}
          />
        </FormGroup>
      )}

      {(adType === 'banner' || adType === 'sponsored_brand') && (
        <FormGroup className="border-top pt-2">
          <Label
            htmlFor="budget.cpm"
            isRequired
            helperText={t('rm:cpm.description')}
          >
            CPM Max
            {data?.min_cpm >= 0
              ? ` (${t('rm:minimum')}: ${formatMoney(data.min_cpm)})`
              : null}
          </Label>
          <InputMoney
            id="budget.cpm"
            name="budget.cpm"
            required
            disabled={!isReady}
            onChange={data => handleCost(data, 'cpm')}
            showBadgeIsValid={CPM >= data?.min_cpm}
            showBadgeIsInvalid={CPM < data?.min_cpm}
          />
        </FormGroup>
      )}

      {!!selectedAudience && (
        <section className="d-flex align-items-center gap-1 border-top pt-2">
          <div className="d-flex gap-1">
            <span role="strong" className="reach-title fw-semibold fs-6">
              {t('rm:AudienceCard.text.audienceCost')}:
            </span>
            <span className="fs-6">
              {formatMoneyV2({ value: selectedAudience.price })}
            </span>
          </div>
          <div className="d-flex align-items-center ">
            <Badge
              size="small"
              template={selectedAudience.price ? 'warning' : 'success'}
              text={
                selectedAudience.price
                  ? t('rm:AudienceCard.text.audienceCost.fixedCost')
                  : t('rm:AudienceCard.text.audienceCost.noCost')
              }
            />
            <Tooltip
              icon={<FiHelpCircle />}
              text={t('rm:AudienceCard.text.audienceCostDescription')}
            />
          </div>
        </section>
      )}
    </fieldset>
  )
}

export default StandardInvestment
